import { LegalShell } from "@/components/app/LegalShell";

const SECTION_H = "text-lg font-display font-semibold tracking-tight mt-8 mb-2";

export default function TermsAndConditionsPage() {
    return (
        <LegalShell
            overline="Legal"
            title="Terms & Conditions"
            lastUpdated="February 2026"
            testid="terms-page"
        >
            <p>
                These Terms &amp; Conditions (&ldquo;Terms&rdquo;) govern your access to and use of FormFlo (the &ldquo;Service&rdquo;), operated by{" "}
                <strong>Rishmathi Tech Ventures Pvt Ltd</strong> (&ldquo;Rishmathi&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;).
                By signing up or using the Service, you agree to these Terms on behalf of yourself and the organization you represent.
            </p>

            <h2 className={SECTION_H}>1. Eligibility</h2>
            <p>
                You must be at least 18 years of age and authorised to bind your organization to a commercial contract.
                One natural person may not register multiple paid accounts to evade quotas.
            </p>

            <h2 className={SECTION_H}>2. Subscription &amp; billing</h2>
            <ul className="list-disc pl-5 space-y-1.5">
                <li>Subscriptions are sold on an <strong>annual</strong> basis (monthly price × 12, billed upfront).</li>
                <li>The platform <strong>does not charge GST or any other tax</strong>; the displayed price is the final amount payable.</li>
                <li>Additional &ldquo;expansion packs&rdquo; can be purchased at any time during a subscription period.</li>
                <li>All payments are processed by Zoho Payments. Once a payment is captured, your subscription is activated immediately.</li>
                <li>Failure to renew at the end of a term will move your organization to a read-only state for 30 days, after which data is permanently deleted (see Privacy Policy §6).</li>
            </ul>

            <h2 className={SECTION_H}>3. Fair Usage Policy</h2>
            <p>
                FormFlo is offered on a fair-usage basis to ensure consistent performance for every customer:
            </p>
            <ul className="list-disc pl-5 space-y-1.5">
                <li>Hard quotas — users, forms, records and storage — are defined by your current pack count and shown on your Billing page.</li>
                <li>Burst limits: no more than <strong>5 requests/second</strong> per user and <strong>120 logins/hour</strong> per email/IP. Bulk imports and exports should be scheduled during off-peak hours.</li>
                <li>Form records must not exceed an average of <strong>50 KB</strong> per record; any single record larger than 1 MB may be truncated or rejected.</li>
                <li>Public form links must be used for genuine business intake only — not as a free CAPTCHA-protected file relay or anonymous mass-mail target.</li>
                <li>Automation traffic (bots, scrapers) is only allowed against your own tenant data using the documented API with your own credentials.</li>
                <li>We may throttle or temporarily suspend usage that materially degrades service for other tenants. We will notify you before suspension wherever practicable.</li>
            </ul>

            <h2 className={SECTION_H}>4. Prohibited content — sensitive data</h2>
            <p className="border-l-4 border-destructive/60 bg-destructive/5 pl-4 py-3">
                <strong>You must NOT store sensitive data</strong> in FormFlo form fields, attachments, comments or any other input.
                Prohibited data includes (but is not limited to):
            </p>
            <ul className="list-disc pl-5 space-y-1.5">
                <li>Passwords, PINs, OTPs, security answers, API keys or any authentication secret;</li>
                <li>Full credit / debit card numbers, CVV, card expiry, UPI PIN, net-banking or wallet credentials;</li>
                <li>Aadhaar, PAN, passport or any government ID number (unless your workflow is separately compliance-certified and you have written permission from Rishmathi);</li>
                <li>Medical records, biometric data, sexual orientation, religion, or any &ldquo;special category&rdquo; under DPDP Act 2023 / GDPR;</li>
                <li>Content that is illegal, defamatory, hateful, sexually explicit involving minors, or that infringes third-party IP.</li>
            </ul>
            <p>
                You are solely responsible for the data you upload. We reserve the right to redact, quarantine, or delete prohibited content
                and to suspend or terminate organizations that repeatedly violate this clause. We are not liable for damages arising from your
                violation of this section.
            </p>

            <h2 className={SECTION_H}>5. Your responsibilities</h2>
            <ul className="list-disc pl-5 space-y-1.5">
                <li>Keep login credentials confidential; you are responsible for actions performed under your account.</li>
                <li>Maintain accurate user lists; remove ex-employees promptly.</li>
                <li>Configure RBAC, visibility and PIN-gating in line with your data-sensitivity needs.</li>
                <li>Comply with all applicable laws (data protection, electronic records, sectoral regulations).</li>
            </ul>

            <h2 className={SECTION_H}>6. Intellectual property</h2>
            <p>
                FormFlo, its source code, branding, and documentation remain the property of Rishmathi Tech Ventures Pvt Ltd.
                Your form metadata, records and content remain <strong>yours</strong>; you grant us only the limited rights needed
                to host and operate the Service on your behalf.
            </p>

            <h2 className={SECTION_H}>7. Service availability</h2>
            <p>
                We target 99.5% monthly uptime excluding scheduled maintenance, force majeure and third-party outages (Zoho Payments, Zoho ZeptoMail, MongoDB Atlas, cloud infrastructure).
                We do not warrant that the Service will be uninterrupted or error-free.
            </p>

            <h2 className={SECTION_H}>8. Termination</h2>
            <ul className="list-disc pl-5 space-y-1.5">
                <li>You may cancel at any time from your Billing page or by writing to <a href="mailto:contact@rishmathi.com" className="text-primary hover:underline">contact@rishmathi.com</a>. Refunds are governed by the Refund Policy.</li>
                <li>We may terminate immediately for material breach (prohibited content, security abuse, non-payment, repeated FUP violations).</li>
                <li>On termination, the rights and licenses granted to you cease. Data retention follows the Privacy Policy.</li>
            </ul>

            <h2 className={SECTION_H}>9. Limitation of liability</h2>
            <p>
                To the maximum extent permitted by law, our aggregate liability for any claim relating to the Service is limited to the
                fees actually paid by your organization to Rishmathi in the <strong>12 months</strong> preceding the claim.
                We are not liable for indirect, incidental or consequential damages, including loss of profits, goodwill or data.
            </p>

            <h2 className={SECTION_H}>10. Governing law &amp; jurisdiction</h2>
            <p>
                These Terms are governed by the laws of India. Any dispute will be subject to the exclusive jurisdiction of
                the competent courts at <strong>Hyderabad, Telangana</strong>.
            </p>

            <h2 className={SECTION_H}>11. Changes to the Terms</h2>
            <p>
                We may revise these Terms. We will notify Org Admins by email of any material change at least 15 days before it takes effect.
                Continued use after the effective date constitutes acceptance.
            </p>

            <h2 className={SECTION_H}>12. Contact</h2>
            <p>
                Rishmathi Tech Ventures Pvt Ltd · Hyderabad, Telangana, India ·{" "}
                <a href="mailto:contact@rishmathi.com" className="text-primary hover:underline">contact@rishmathi.com</a>
            </p>
        </LegalShell>
    );
}
